import { useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import {
  useCreateDiseaseArea, useDeleteDiseaseArea, useDiseaseAreas,
  useUpdateDiseaseArea,
} from "@/api/hooks/diseaseAreas";
import type { DiseaseArea } from "@/api/types";

interface FormState {
  code: string;
  name: string;
}

/**
 * Therapeutic-areas editor. Lists every DiseaseArea row with edit /
 * delete actions and a top-bar create form. Delete is a hard delete
 * via the backend — the backend will surface FK violations as 4xx if
 * a DA still has dependents.
 */
export function TherapeuticAreasPage() {
  const query = useDiseaseAreas();
  const create = useCreateDiseaseArea();
  const remove = useDeleteDiseaseArea();
  const [createForm, setCreateForm] = useState<FormState>({ code: "", name: "" });
  const [editing, setEditing] = useState<DiseaseArea | null>(null);

  const rows = query.data ?? [];

  const onCreate = () => {
    const code = createForm.code.trim();
    const name = createForm.name.trim();
    if (!code || !name) return;
    create.mutate(
      { code, name },
      {
        onSuccess: () => setCreateForm({ code: "", name: "" }),
      },
    );
  };

  const onDelete = (da: DiseaseArea) => {
    if (!window.confirm(`Delete therapeutic area "${da.name}"?`)) return;
    remove.mutate(da.id);
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Therapeutic areas
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Drives the disease-area tabs on every site detail page and the
        cohort scope for milestone / personnel views.
      </Typography>

      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 3, flexWrap: "wrap" }}>
        <TextField
          size="small"
          label="Code"
          placeholder="LD"
          value={createForm.code}
          onChange={(e) => setCreateForm((s) => ({ ...s, code: e.target.value }))}
          sx={{ width: 110 }}
        />
        <TextField
          size="small"
          label="Name"
          placeholder="Liver Disease"
          value={createForm.name}
          onChange={(e) => setCreateForm((s) => ({ ...s, name: e.target.value }))}
          sx={{ minWidth: 240, flex: 1 }}
        />
        <Button
          variant="contained"
          onClick={onCreate}
          disabled={!createForm.code.trim() || !createForm.name.trim() || create.isPending}
        >
          {create.isPending ? "Adding…" : "Add therapeutic area"}
        </Button>
      </Box>

      {create.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {create.error.message}
        </Alert>
      )}

      {query.isLoading && (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <CircularProgress size={20} />
        </Box>
      )}
      {query.error && (
        <Alert severity="error">
          Could not load therapeutic areas: {(query.error as Error).message}
        </Alert>
      )}
      {!query.isLoading && rows.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 2 }}>
          No therapeutic areas yet — add one above.
        </Typography>
      )}
      {rows.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 110 }}>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell align="right" sx={{ width: 110 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((da) => (
              <TableRow key={da.id} hover>
                <TableCell sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
                  {da.code}
                </TableCell>
                <TableCell>{da.name}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} sx={{ justifyContent: "flex-end" }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setEditing(da)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => onDelete(da)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {editing && (
        <EditDialog
          key={editing.id}
          da={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </Box>
  );
}

function EditDialog({
  da, onClose,
}: { da: DiseaseArea; onClose: () => void }) {
  const [name, setName] = useState(da.name);
  const update = useUpdateDiseaseArea(da.id);
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit therapeutic area · {da.code}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Name"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        {update.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {update.error.message}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim() || update.isPending}
          onClick={() => {
            update.mutate({ name: name.trim() }, { onSuccess: onClose });
          }}
        >
          {update.isPending ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
